import { Form } from '../Form/Form'
import { ChoosePrimaryKeywordProps } from './Types'
import Styles from './SEOPlanningWizard.module.scss'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight } from 'almanac-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import Table from 'components/BlazeTable/BlazeTable'
import { KeywordItem } from './KeywordsResponseType'
import { Heading } from 'blaze-ui/Heading/Heading'
import { BasicKeywordRow, KeywordRow } from './KeywordRow'
import { KeywordsLoadingState } from './KeywordsLoadingState'
import { AddKeywordsInput } from './AddKeywordsInput'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export const ChoosePrimaryKeyword = ({
  generateKeywordsMutation,
  selectedKeyword,
  setSelectedKeyword,
  onSubmit,
  goBack,
  addedKeywords,
  setAddedKeywords,
}: ChoosePrimaryKeywordProps) => {
  const { isMobile } = useResponsiveDesign()

  const toggleKeyword = (keyword: string) => {
    setSelectedKeyword(keyword)
  }

  const addKeyword = (keyword: string) => {
    setAddedKeywords([...addedKeywords, keyword])
  }

  const keywordsResponses = generateKeywordsMutation.data
  const keywordItems = keywordsResponses?.reduce((acc, keywordsResponse) => {
    if (!keywordsResponse.tasks[0].result[0].items) return acc

    return acc.concat(keywordsResponse.tasks[0].result[0].items)
  }, [] as KeywordItem[])
  const keywords = keywordItems?.map((item) => item.keyword_data)
  const keywordsWithoutDuplicates = keywords?.filter(
    (keyword, index, self) => self.findIndex((k) => k.keyword === keyword.keyword) === index,
  )
  const keywordsSorted = keywordsWithoutDuplicates?.sort(
    (a, b) => b.keyword_info.search_volume - a.keyword_info.search_volume,
  )
  const maxSearchVolume = keywordsSorted?.[0]?.keyword_info.search_volume || Infinity

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <Form.Fields>
            <div className={Styles.header}>
              <Heading level={4}>Select Your Primary Keyword</Heading>
              <Text variant="secondary">
                Your primary keyword will shape your content and drive SEO performance. Search volume is approximately
                how often a keyword is searched each month. Difficulty tells you how hard it will be to rank on the
                first page of results.
              </Text>
            </div>
            <Table variant="light">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Keyword</Table.Th>
                  <Table.Th maxWidth={isMobile ? '66px' : '148px'}>Volume</Table.Th>
                  <Table.Th maxWidth="66px">Difficulty</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {generateKeywordsMutation.isLoading && <KeywordsLoadingState />}
                {!!keywordsSorted && !!keywordsSorted.length ? (
                  keywordsSorted.map((item) => (
                    <KeywordRow
                      key={item.keyword}
                      item={item}
                      maxSearchVolume={maxSearchVolume}
                      isSelected={item.keyword === selectedKeyword}
                      toggleKeyword={toggleKeyword}
                    />
                  ))
                ) : (
                  <>
                    {!generateKeywordsMutation.isLoading && !addedKeywords.length && (
                      <Table.Tr>
                        <Table.Td>
                          <Text variant="secondary">No keywords found for this site, try adding your own.</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </>
                )}
                {addedKeywords.map((keyword) => (
                  <BasicKeywordRow
                    key={keyword}
                    keyword={keyword}
                    isSelected={keyword === selectedKeyword}
                    toggleKeyword={toggleKeyword}
                  />
                ))}
              </Table.Tbody>
            </Table>
            <AddKeywordsInput onAddKeyword={addKeyword} />
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>

      <Form.Actions isSticky>
        {!isMobile && <Form.PreviousButton onPress={goBack}>Choose Websites</Form.PreviousButton>}
        <Form.PrimaryActions>
          <Button size="lg" onPress={onSubmit} endIcon={ArrowRight} isDisabled={!selectedKeyword} fullWidth={isMobile}>
            Select Secondary Keywords
          </Button>
          {isMobile && (
            <Button frontIcon={ArrowLeft} variant="secondary" size="lg" onPress={goBack} fullWidth={isMobile}>
              Choose Websites
            </Button>
          )}
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}
